import { Vector2, Vector3 } from 'three';

import { clamp, degreesToRadians } from '../../../../utilities/math-helpers';
import { getArcPoint, getSweep, splitBulge } from './arc';

/**
 * A curve between two points: a rational quadratic Bézier (a conic), defined in skymap-oda's
 * shared/cpp/glb/README.md. It covers circular arcs and ellipses exactly, plain quadratic Béziers
 * (weight 1) and everything between:
 *
 *   C(t) = ((1-t)² P0 + 2t(1-t) w P1 + t² P2) / ((1-t)² + 2t(1-t) w + t²)
 *
 * with P0 the start, P1 the control point, P2 the end and w the weight, more than 0.
 * The curve starts towards the control point and ends coming from it, and stays inside the
 * triangle of the three points.
 *
 * NOTE: The calculations of the point and the length are duplicated in the shader of the styled
 * lines (styled-lines-material.ts), keep them in sync.
 */
export type Conic = {
  start: Vector3;
  control: Vector3;
  end: Vector3;
  weight: number;
};

/**
 * Circular arcs are split into pieces of at most this sweep, since one curve can't be half a circle
 * or more. The weight of an arc is cos(sweep / 2), at least 0.087 here, where the length along the
 * curve in the shader (for the stripes) is still within 0.02% of the arc's length.
 */
const maxArcSweep = degreesToRadians(170);

/**
 * Gauss-Legendre points and weights on [-1, 1], for the length along the curve.
 */
const gaussPoints = [
  0, -0.5384693101056831, 0.5384693101056831, -0.906179845938664, 0.906179845938664,
];
const gaussWeights = [
  0.5688888888888889, 0.4786286704993665, 0.4786286704993665, 0.2369268850561891,
  0.2369268850561891,
];

/**
 * Gets the point at t (0 to 1) along the curve. t is not proportional to the length.
 */
export function getConicPoint(
  { start, control, end, weight }: Conic,
  t: number,
  target = new Vector3(),
): Vector3 {
  const a = (1 - t) * (1 - t);
  const b = 2 * t * (1 - t) * weight;
  const c = t * t;

  return target
    .copy(start)
    .multiplyScalar(a)
    .addScaledVector(control, b)
    .addScaledVector(end, c)
    .divideScalar(a + b + c);
}

/**
 * Gets the derivative of the curve at t.
 */
export function getConicDerivative({ start, control, end, weight }: Conic, t: number): Vector3 {
  const a = (1 - t) * (1 - t);
  const b = 2 * t * (1 - t) * weight;
  const c = t * t;
  const denominator = a + b + c;
  const denominatorDerivative = 2 * (1 - t) * (weight - 1) + 2 * t * (1 - weight);

  const numerator = start
    .clone()
    .multiplyScalar(a)
    .addScaledVector(control, b)
    .addScaledVector(end, c);
  const numeratorDerivative = control
    .clone()
    .multiplyScalar(weight)
    .sub(start)
    .multiplyScalar(2 * (1 - t))
    .add(
      end
        .clone()
        .sub(control.clone().multiplyScalar(weight))
        .multiplyScalar(2 * t),
    );

  return numeratorDerivative
    .multiplyScalar(denominator)
    .sub(numerator.multiplyScalar(denominatorDerivative))
    .divideScalar(denominator * denominator);
}

/**
 * Gets the direction of the curve at t in the horizontal plane, going from start to end.
 */
export function getConicDirection(conic: Conic, t: number): Vector2 {
  const derivative = getConicDerivative(conic, t);
  const direction = new Vector2(derivative.x, derivative.y);

  return direction.lengthSq() > 0
    ? direction.normalize()
    : new Vector2(conic.end.x - conic.start.x, conic.end.y - conic.start.y).normalize();
}

/**
 * Gets the length along the curve from t0 to t1, or in the horizontal plane when planar.
 */
export function getConicLength(conic: Conic, t0 = 0, t1 = 1, planar = false): number {
  // Gauss-Legendre on a few pieces, which is accurate for the weights that are used.
  const pieces = 8;
  const step = (t1 - t0) / pieces;
  let length = 0;

  for (let piece = 0; piece < pieces; piece++) {
    const center = t0 + step * (piece + 0.5);

    for (let i = 0; i < gaussPoints.length; i++) {
      const derivative = getConicDerivative(conic, center + (step / 2) * gaussPoints[i]);

      if (planar) {
        derivative.setZ(0);
      }

      length += gaussWeights[i] * derivative.length() * (step / 2);
    }
  }

  return Math.abs(length);
}

/**
 * Gets t (0 to 1) of the point on the curve that is closest to the point in the horizontal plane.
 */
export function getConicParameter(conic: Conic, point: Vector3): number {
  const distanceSq = (t: number) => {
    const p = getConicPoint(conic, t);
    return (p.x - point.x) ** 2 + (p.y - point.y) ** 2;
  };

  // Find the closest of a few samples, then narrow it down around it.
  const samples = 32;
  let best = 0;

  for (let i = 1; i <= samples; i++) {
    if (distanceSq(i / samples) < distanceSq(best / samples)) {
      best = i;
    }
  }

  let low = Math.max(0, (best - 1) / samples);
  let high = Math.min(1, (best + 1) / samples);

  for (let i = 0; i < 60; i++) {
    const a = low + (high - low) / 3;
    const b = high - (high - low) / 3;

    if (distanceSq(a) < distanceSq(b)) {
      high = b;
    } else {
      low = a;
    }
  }

  return clamp((low + high) / 2, 0, 1);
}

/**
 * Splits the curve at t into two curves that follow it, both from start to end.
 */
export function splitConic(conic: Conic, t: number): [Conic, Conic] {
  // De Casteljau in homogeneous coordinates, where the curve is a plain quadratic Bézier.
  const { start, control, end, weight } = conic;
  const homogeneous = (p: Vector3, w: number) => ({ p: p.clone().multiplyScalar(w), w });
  const lerp = (a: { p: Vector3; w: number }, b: { p: Vector3; w: number }) => ({
    p: a.p.clone().lerp(b.p, t),
    w: a.w + (b.w - a.w) * t,
  });

  const p0 = homogeneous(start, 1);
  const p1 = homogeneous(control, weight);
  const p2 = homogeneous(end, 1);
  const q0 = lerp(p0, p1);
  const q1 = lerp(p1, p2);
  const middle = lerp(q0, q1);
  const point = middle.p.clone().divideScalar(middle.w);

  // Normalized so that the weights of the ends are 1.
  return [
    {
      start: start.clone(),
      control: q0.p.clone().divideScalar(q0.w),
      end: point.clone(),
      weight: q0.w / Math.sqrt(middle.w),
    },
    {
      start: point,
      control: q1.p.clone().divideScalar(q1.w),
      end: end.clone(),
      weight: q1.w / Math.sqrt(middle.w),
    },
  ];
}

/**
 * Gets the part of the curve from t0 to t1, as a curve of its own from start to end.
 */
export function getConicPart(conic: Conic, t0: number, t1: number): Conic {
  const { start, control, end, weight } = conic;

  // The blossom of the curve in homogeneous coordinates, where it is a plain quadratic Bézier:
  // its points at (t, t) are on the curve, and (t0, t1) is the control point of the part.
  const blossom = (a: number, b: number) => {
    const c0 = (1 - a) * (1 - b);
    const c1 = ((1 - a) * b + a * (1 - b)) * weight;
    const c2 = a * b;
    const w = c0 + c1 + c2;
    const p = start
      .clone()
      .multiplyScalar(c0)
      .addScaledVector(control, c1)
      .addScaledVector(end, c2);
    return { point: p.divideScalar(w), w };
  };

  const first = blossom(t0, t0);
  const middle = blossom(t0, t1);
  const last = blossom(t1, t1);

  // Normalized so that the weights of the ends are 1.
  return {
    start: first.point,
    control: middle.point,
    end: last.point,
    weight: middle.w / Math.sqrt(first.w * last.w),
  };
}

/**
 * Gets t of the points at the distances along the curve, which are in increasing order.
 */
export function getConicParametersAtLengths(conic: Conic, distances: number[]): number[] {
  // A table of the lengths at even steps of t, which the distances are found in, and then refined
  // with Newton's method, since the length is a smooth function of t.
  const steps = 256;
  const lengths = [0];

  for (let i = 1; i <= steps; i++) {
    lengths.push(lengths[i - 1] + getConicLength(conic, (i - 1) / steps, i / steps));
  }

  let step = 0;

  return distances.map((distance) => {
    while (step < steps - 1 && lengths[step + 1] < distance) {
      step++;
    }

    const fraction = (distance - lengths[step]) / (lengths[step + 1] - lengths[step] || 1);
    let t = clamp((step + fraction) / steps, 0, 1);

    for (let i = 0; i < 2; i++) {
      const speed = getConicDerivative(conic, t).length();
      if (speed > 0) {
        t = clamp(t - (getConicLength(conic, 0, t) - distance) / speed, 0, 1);
      }
    }

    return t;
  });
}

/**
 * Gets the distance from the chord to the middle of the curve.
 */
export function getConicSagitta({ start, control, end, weight }: Conic): number {
  const middle = start.clone().add(end).multiplyScalar(0.5);
  // C(0.5) = (M + w P1) / (1 + w)
  return (control.distanceTo(middle) * weight) / (1 + weight);
}

/**
 * Gets the number of steps of equal t needed for the steps to be at most the tolerance from the curve.
 */
export function getConicSegmentCount(conic: Conic, tolerance: number, maxSegments = 1024) {
  // The distance of n steps from the curve is about sagitta / n².
  const segments = Math.ceil(Math.sqrt((2 * getConicSagitta(conic)) / tolerance));
  return clamp(segments, 1, maxSegments);
}

/**
 * Gets the points along the curve, from start to end, for the given number of steps.
 */
export function tessellateConic(conic: Conic, segments: number): Vector3[] {
  const points: Vector3[] = [];

  for (let i = 0; i <= segments; i++) {
    points.push(getConicPoint(conic, i / segments));
  }

  return points;
}

/**
 * Gets the points along the curve as pairs, for rendering with line segments.
 */
export function tessellateConicAsLineSegments(conic: Conic, segments: number): Vector3[] {
  const points = tessellateConic(conic, segments);
  return points.slice(1).flatMap((point, i) => [points[i], point]);
}

/**
 * Gets a radius of a sphere around the middle of the chord that contains the whole curve,
 * which stays inside the triangle of the start, control point and end.
 */
export function getConicBoundingRadius({ start, control, end }: Conic): number {
  const middle = start.clone().add(end).multiplyScalar(0.5);
  return Math.max(middle.distanceTo(start), middle.distanceTo(control));
}

/**
 * Gets the center of the ellipse that the curve is an arc of, which is a circle for a circular
 * arc, or undefined for a parabola or a hyperbola (weight 1 or more):
 * (M - w² P1) / (1 - w²), with M the middle of the chord.
 */
export function getConicCenter({ start, control, end, weight }: Conic): Vector3 | undefined {
  if (weight <= 0 || weight >= 1 - 1e-9) {
    return undefined;
  }

  const weightSq = weight * weight;
  return start
    .clone()
    .add(end)
    .multiplyScalar(0.5)
    .addScaledVector(control, -weightSq)
    .divideScalar(1 - weightSq);
}

/**
 * Gets the center of the curve if it is a circular arc in the horizontal plane, at the height of
 * the middle of its chord, otherwise undefined.
 *
 * An arc has its control point where the tangents at its ends meet, as far from both ends, and
 * weight cos(a) for the angle a between the chord and the tangents.
 */
export function getConicCircleCenter({ start, control, end, weight }: Conic): Vector3 | undefined {
  const middle = start.clone().add(end).multiplyScalar(0.5);
  const toStart = Math.hypot(start.x - control.x, start.y - control.y);
  const toEnd = Math.hypot(end.x - control.x, end.y - control.y);
  const halfChord = Math.hypot(start.x - middle.x, start.y - middle.y);
  const sagitta = Math.hypot(control.x - middle.x, control.y - middle.y);
  const tolerance = 1e-3;

  if (
    sagitta < 1e-9 ||
    Math.abs(toStart - toEnd) > tolerance * toStart ||
    Math.abs(weight - halfChord / toStart) > tolerance
  ) {
    return undefined;
  }

  // The center is on the other side of the chord from the control point, as far from the middle
  // of the chord as halfChord² / sagitta (the radii to the ends are perpendicular to the tangents).
  const distance = (halfChord * halfChord) / sagitta;

  return new Vector3(
    middle.x + ((middle.x - control.x) / sagitta) * distance,
    middle.y + ((middle.y - control.y) / sagitta) * distance,
    middle.z,
  );
}

/**
 * Gets t (0 to 1) of where the line through the origin in the direction crosses the curve,
 * in the horizontal plane.
 */
export function intersectConicWithLine2d(
  conic: Conic,
  origin: Vector3,
  direction: Vector2,
): number[] {
  const { start, control, end, weight } = conic;
  // The side of the line that a point is on, which is 0 on the line.
  const normal = new Vector2(-direction.y, direction.x);
  const side = (p: Vector3) => normal.x * (p.x - origin.x) + normal.y * (p.y - origin.y);

  // The numerator of side(C(t)) is a quadratic in t: (1-t)² s0 + 2t(1-t) w s1 + t² s2 = 0.
  const s0 = side(start);
  const s1 = side(control) * weight;
  const s2 = side(end);
  const a = s0 - 2 * s1 + s2;
  const b = 2 * (s1 - s0);
  const c = s0;

  const solutions =
    Math.abs(a) < 1e-12
      ? Math.abs(b) < 1e-12
        ? []
        : [-c / b]
      : (() => {
          const discriminant = b * b - 4 * a * c;

          if (discriminant < 0) {
            return [];
          }

          const root = Math.sqrt(discriminant);
          return [(-b - root) / (2 * a), (-b + root) / (2 * a)];
        })();

  return solutions.filter((t) => t >= 0 && t <= 1);
}

/**
 * Gets the curve of a circular arc with a bulge (see arc.ts), of a sweep less than 180 degrees.
 */
export function getConicFromBulge(start: Vector3, end: Vector3, bulge: number): Conic {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const chordLength = Math.hypot(dx, dy);
  const offset = (chordLength * bulge) / (1 - bulge * bulge);
  const middle = start.clone().add(end).multiplyScalar(0.5);

  return {
    start: start.clone(),
    // To the right of the chord for a positive bulge.
    control: middle.add(new Vector3(dy / chordLength, -dx / chordLength, 0).multiplyScalar(offset)),
    end: end.clone(),
    weight: (1 - bulge * bulge) / (1 + bulge * bulge),
  };
}

/**
 * Gets the curves of a circular arc with a bulge (see arc.ts), in pieces of equal sweep so that
 * each can be one curve. The pieces go from the start to the end.
 */
export function getArcConics(start: Vector3, end: Vector3, bulge: number): Conic[] {
  const pieces = Math.max(1, Math.ceil(Math.abs(getSweep(bulge)) / maxArcSweep));
  const points = Array.from({ length: pieces + 1 }, (_, i) =>
    getArcPoint(start, end, bulge, i / pieces),
  );
  const [pieceBulge] = splitBulge(bulge, 1 / pieces);

  return points.slice(1).map((point, i) => getConicFromBulge(points[i], point, pieceBulge));
}

/**
 * Gets the four quarter circles of a circle in the horizontal plane, counterclockwise from +X.
 */
export function getCircleConics(center: Vector3, radius: number): Conic[] {
  const corners = [
    [1, 0],
    [0, 1],
    [-1, 0],
    [0, -1],
  ].map(([x, y]) => center.clone().add(new Vector3(x * radius, y * radius, 0)));

  return corners.map((start, i) => {
    const end = corners[(i + 1) % 4];
    return {
      start: start.clone(),
      // Where the tangents at the ends meet, the corner of the square around the circle.
      control: new Vector3(
        Math.abs(start.x - center.x) > Math.abs(end.x - center.x) ? start.x : end.x,
        Math.abs(start.y - center.y) > Math.abs(end.y - center.y) ? start.y : end.y,
        center.z,
      ),
      end: end.clone(),
      weight: Math.SQRT1_2,
    };
  });
}

/**
 * Gets the curves of a segment from start to end as a file has it: a control point and weight, or a
 * bulge in older files, which may need several curves.
 */
export function getCurveConics(
  start: Vector3,
  end: Vector3,
  curve: { control: Vector3; weight: number } | { bulge: number },
): Conic[] {
  return 'bulge' in curve
    ? getArcConics(start, end, curve.bulge)
    : [{ start, control: curve.control, end, weight: curve.weight }];
}

/**
 * The curve of a design line segment, relative to the segment, so that it keeps its shape when the
 * start or end moves: it is moved, turned and scaled with them, like the bulge of an arc.
 */
export type SegmentCurve = {
  /** Where the control point is along the chord, from 0 at the start to 1 at the end. */
  along: number;
  /** How far the control point is to the right of the chord, in chord lengths, horizontally. */
  right: number;
  /** How far the control point is above the straight line between the start and end, in meters. */
  up: number;
  weight: number;
};

/**
 * Gets the curve from start to end with the curve of a segment.
 */
export function getSegmentConic(start: Vector3, end: Vector3, curve: SegmentCurve): Conic {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const height = start.z + (end.z - start.z) * curve.along + curve.up;

  return {
    start: start.clone(),
    // The right of the chord (dx, dy) is (dy, -dx).
    control: new Vector3(
      start.x + dx * curve.along + dy * curve.right,
      start.y + dy * curve.along - dx * curve.right,
      height,
    ),
    end: end.clone(),
    weight: curve.weight,
  };
}

/**
 * Gets the curve of a segment from a curve, or undefined if its start and end are at the same place
 * horizontally, where the curve can't follow them.
 */
export function getSegmentCurve({ start, control, end, weight }: Conic): SegmentCurve | undefined {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSq = dx * dx + dy * dy;

  if (lengthSq < 1e-18) {
    return undefined;
  }

  const along = ((control.x - start.x) * dx + (control.y - start.y) * dy) / lengthSq;
  const right = ((control.x - start.x) * dy - (control.y - start.y) * dx) / lengthSq;
  const up = control.z - (start.z + (end.z - start.z) * along);

  return { along, right, up, weight };
}

/**
 * Gets the curve of a segment in the opposite direction, from its end to its start.
 */
export function reverseSegmentCurve({ along, right, up, weight }: SegmentCurve): SegmentCurve {
  return { along: 1 - along, right: -right, up, weight };
}
